import { ListIcon, XIcon } from '@phosphor-icons/react';
import { useQuery } from '@tanstack/react-query';
import * as React from 'react';
import { Drawer } from 'vaul';
import CONSTANTS from '../../Constants';
import {
    isNavCategoryWithGarments,
    NAV_CATEGORY_IDS,
    NAV_CATEGORY_TITLES,
    NavCategoryId,
    useGetCategories,
} from '../../Hooks/useGetCategories';
import { garmentService } from '../../Services';
import { getUrlForEnvironment } from '../../Utils/environment';
import { POCToggle } from '../POCToggle';
import Button from '../ui/Button/Button';
import { Divider } from '../ui/Divider/Divider';
import Link from '../ui/Link/Link';
import { Skeleton } from '../ui/Skeleton';
import styles from './side-nav.module.scss';

interface SideNavProps {
    handleLogout: () => void;
}

export const SideNav = ({ handleLogout }: SideNavProps) => {
    const [open, setOpen] = React.useState(false);
    const closeDrawer = () => setOpen(false);
    const getManageAccountURL = () => getUrlForEnvironment(CONSTANTS.MANAGE_ACCOUNT_URL);

    return (
        <Drawer.Root direction="left" modal={true} open={open} onOpenChange={setOpen}>
            <Drawer.Trigger className={`${styles.Button}`}>
                <ListIcon size={32} />
            </Drawer.Trigger>
            <Drawer.Portal>
                <Drawer.Overlay className={styles.Overlay} />
                <Drawer.Content className={styles.Content}>
                    <Drawer.Close className={styles.close}>
                        <XIcon size={32} weight="thin" />
                    </Drawer.Close>
                    <nav className={styles.nav}>
                        <div className={styles.navItems}>
                            <CategoryNavItems onNavigate={closeDrawer} />
                            <OutfitsNavItem onNavigate={closeDrawer} />
                        </div>
                        <div className={styles.navItems}>
                            <Divider />
                            {CONSTANTS.IS_DEV || CONSTANTS.IS_STAGE ? (
                                <div className="pocToggle">
                                    <label htmlFor="poc-toggle">Enable POC</label>
                                    <POCToggle />
                                </div>
                            ) : null}
                            <Button asChild variant="text">
                                <a
                                    href={getManageAccountURL()}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ textDecoration: 'none', color: 'inherit' }}
                                >
                                    Manage Account
                                </a>
                            </Button>

                            <Button variant="text" onClick={handleLogout}>
                                Logout
                            </Button>
                        </div>
                    </nav>
                </Drawer.Content>
            </Drawer.Portal>
        </Drawer.Root>
    );
};

interface CategoryNavItemsProps {
    onNavigate: () => void;
}

const CategoryNavItems = ({ onNavigate }: CategoryNavItemsProps) => {
    const { isLoading, isError, data } = useGetCategories();

    if (isLoading) {
        return (
            <>
                {NAV_CATEGORY_IDS.map((id) => (
                    <Skeleton key={id} width={42} height={24} />
                ))}
            </>
        );
    }

    const visibleItems: { id: NavCategoryId; name: string }[] = isError
        ? NAV_CATEGORY_IDS.map((id) => ({ id, name: NAV_CATEGORY_TITLES[id] }))
        : (data?.categories.filter(isNavCategoryWithGarments) ?? []);

    return (
        <>
            {visibleItems.map((category) => (
                <Button key={category.id} asChild variant="text" onClick={onNavigate}>
                    <Link
                        to={`/collections/${category.id}`}
                        style={{ textDecoration: 'none', color: 'inherit' }}
                    >
                        {category.name}
                    </Link>
                </Button>
            ))}
        </>
    );
};

interface OutfitsNavItemProps {
    onNavigate: () => void;
}

const OutfitsNavItem = ({ onNavigate }: OutfitsNavItemProps) => {
    const { isPending, data } = useQuery({
        queryKey: ['outfits', 'all'],
        queryFn: () => garmentService.fetchAllOutfits(),
    });

    if (isPending) {
        return <Skeleton width={42} height={24} />;
    }

    const hasAvailableOutfit = data?.outfits.some((outfit) => outfit.status === 'AVAILABLE');
    if (!hasAvailableOutfit) {
        return null;
    }

    return (
        <Button asChild variant="text" onClick={onNavigate}>
            <Link to="/outfits" style={{ textDecoration: 'none', color: 'inherit' }}>
                Outfits
            </Link>
        </Button>
    );
};
